import { useEffect, useRef, useCallback } from "react";

interface Particle {
  x: number;
  y: number;
  size: number;
  opacity: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

const MAX_PARTICLES = 30;
const SPAWN_INTERVAL = 30;

export const MouseTrail = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particles = useRef<Particle[]>([]);
  const mousePos = useRef({ x: 0, y: 0 });
  const lastSpawn = useRef(0);
  const rafId = useRef(0);
  const active = useRef(true);

  const isTouchDevice = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
  const prefersReduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const spawnParticle = useCallback(() => {
    if (particles.current.length >= MAX_PARTICLES) {
      particles.current.shift();
    }
    const maxLife = 0.6 + Math.random() * 0.4;
    particles.current.push({
      x: mousePos.current.x,
      y: mousePos.current.y,
      size: 3 + Math.random() * 3,
      opacity: 0.3 + Math.random() * 0.5,
      vx: (Math.random() - 0.5) * 20,
      vy: -(10 + Math.random() * 20),
      life: maxLife,
      maxLife,
    });
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const dt = 1 / 60;
    particles.current = particles.current.filter((p) => {
      p.life -= dt;
      if (p.life <= 0) return false;

      p.x += p.vx * dt;
      p.y += p.vy * dt;

      const progress = 1 - p.life / p.maxLife;
      const currentOpacity = p.opacity * (1 - progress);
      const currentSize = p.size * (1 - progress);

      ctx.beginPath();
      ctx.arc(p.x, p.y, currentSize, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(212, 168, 67, ${currentOpacity})`;
      ctx.fill();
      return true;
    });

    if (active.current) {
      rafId.current = requestAnimationFrame(animate);
    }
  }, []);

  useEffect(() => {
    if (isTouchDevice || prefersReduced || window.innerWidth < 768) return;

    const onMove = (e: MouseEvent) => {
      mousePos.current.x = e.clientX;
      mousePos.current.y = e.clientY;

      const now = performance.now();
      if (now - lastSpawn.current > SPAWN_INTERVAL) {
        lastSpawn.current = now;
        spawnParticle();
      }
    };

    document.addEventListener("mousemove", onMove);
    active.current = true;
    rafId.current = requestAnimationFrame(animate);

    return () => {
      document.removeEventListener("mousemove", onMove);
      active.current = false;
      cancelAnimationFrame(rafId.current);
    };
  }, [animate, spawnParticle, isTouchDevice, prefersReduced]);

  if (isTouchDevice || prefersReduced || (typeof window !== "undefined" && window.innerWidth < 768)) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 99990,
      }}
    />
  );
};
